import {
  Box,
  Chip,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import RemoveIcon from '@mui/icons-material/Remove';
import { NAV_ITEMS } from '@/config/navigation';
import {
  ACCESS_LABELS,
  PERMISSION_SECTIONS,
  ROLE_LABELS,
  ROLE_SUMMARIES,
  type Access,
} from '@/config/rolePermissions';
import type { UserRole } from '@/types/enums.generated';

// QA #84 (PM decision 2026-09-30): a read-only page that states what each
// role can see and do. The page table is derived from config/navigation.ts —
// the same table that drives the sidebar and the route guards — so it cannot
// drift from what users actually get; the action table mirrors the backend
// route guards (config/rolePermissions.ts).

const ROLES: readonly UserRole[] = ['admin', 'sub_admin', 'operator'];

function AccessCell({ access }: { access: Access }) {
  if (typeof access === 'object') {
    return <Typography variant="body2">{access.note}</Typography>;
  }
  if (access === 'none') {
    return (
      <Box display="flex" alignItems="center" gap={0.5} color="text.disabled">
        <RemoveIcon fontSize="small" />
        <Typography variant="body2">{ACCESS_LABELS.none}</Typography>
      </Box>
    );
  }
  return (
    <Chip
      size="small"
      icon={<CheckIcon />}
      label={ACCESS_LABELS[access]}
      color={access === 'all' ? 'success' : 'info'}
      variant={access === 'all' ? 'filled' : 'outlined'}
    />
  );
}

export default function RolePermissions() {
  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        角色權限說明
      </Typography>
      <Typography variant="body2" color="text.secondary" gutterBottom>
        系統有三種角色。以下列出每個角色看得到哪些功能頁面、可以做哪些事；權限由系統固定，此頁僅供查閱。
      </Typography>

      <Paper sx={{ p: 3, mt: 3 }}>
        <Typography variant="h6" gutterBottom>
          角色說明
        </Typography>
        <Box display="flex" flexDirection="column" gap={1.5}>
          {ROLES.map((role) => (
            <Box key={role}>
              <Typography variant="subtitle1" fontWeight={600}>
                {ROLE_LABELS[role]}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {ROLE_SUMMARIES[role]}
              </Typography>
            </Box>
          ))}
        </Box>
      </Paper>

      <Paper sx={{ p: 3, mt: 3 }}>
        <Typography variant="h6" gutterBottom>
          看得到的功能頁面
        </Typography>
        <TableContainer>
          <Table size="small" aria-label="各角色看得到的功能頁面">
            <TableHead>
              <TableRow>
                <TableCell>功能頁面</TableCell>
                {ROLES.map((role) => (
                  <TableCell key={role} align="center">
                    {ROLE_LABELS[role]}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {NAV_ITEMS.map((item) => (
                <TableRow key={item.path}>
                  <TableCell>{item.text}</TableCell>
                  {ROLES.map((role) => {
                    const allowed = !item.roles || item.roles.includes(role);
                    return (
                      <TableCell key={role} align="center">
                        {allowed ? (
                          <CheckIcon color="success" fontSize="small" titleAccess="可進入" />
                        ) : (
                          <RemoveIcon color="disabled" fontSize="small" titleAccess="不可進入" />
                        )}
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* QA #87: one heading for every action table below */}
      <Box sx={{ mt: 5 }}>
        <Typography variant="h5" component="h2" gutterBottom>
          角色權限對照表
        </Typography>
        <Typography variant="body2" color="text.secondary">
          以下列出各角色在各功能的操作權限：「全部」代表不限範圍，「限所屬群組」僅能操作所屬群組的資料，「限自己」僅能操作自己建立的資料。
        </Typography>
      </Box>

      {PERMISSION_SECTIONS.map((section) => (
        <Paper key={section.title} sx={{ p: 3, mt: 3 }}>
          <Typography variant="h6" gutterBottom>
            {section.title}
          </Typography>
          <TableContainer>
            <Table size="small" aria-label={`${section.title}的權限`}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: '40%' }}>可以做的事</TableCell>
                  {ROLES.map((role) => (
                    <TableCell key={role}>{ROLE_LABELS[role]}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {section.rows.map((row) => (
                  <TableRow key={row.action}>
                    <TableCell>{row.action}</TableCell>
                    {ROLES.map((role) => (
                      <TableCell key={role}>
                        <AccessCell access={row.access[role]} />
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      ))}
    </Box>
  );
}
